import debounce from 'lodash/debounce';
import React from 'react';
import { styled } from 'styled-components';
import { theme } from '../theme';
import { typography } from '../typography';

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  debounceInMilliseconds?: number;
  caption: string;
}

export const ThemeTextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps & { className?: string }>((props, ref) => {
  const { debounceInMilliseconds = 300, onChange, value, caption, className, ...rest } = props;

  const [tempValue, setTempValue] = React.useState(value || '');

  const debouncedOnChange = React.useMemo(
    () =>
      debounce((newValue: string) => {
        onChange?.({
          target: { value: newValue } as EventTarget & HTMLTextAreaElement,
        } as React.ChangeEvent<HTMLTextAreaElement>);
      }, debounceInMilliseconds),
    [onChange, debounceInMilliseconds],
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTempValue(e.target.value);
    debouncedOnChange(e.target.value);
  };

  React.useEffect(() => {
    setTempValue(value || '');
  }, [value]);

  React.useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  return (
    <TextAreaContainerStyle className={className}>
      {caption}
      <TextAreaStyle {...rest} value={tempValue} onChange={handleChange} ref={ref} rows={4} />
    </TextAreaContainerStyle>
  );
});

const TextAreaContainerStyle = styled.div`
  position: relative;
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  ${typography('body2')};
`;

const TextAreaStyle = styled.textarea`
  width: 100%;
  ${typography('body1')};
  padding: ${theme.spacing.l};
  border-radius: 40px;
  box-sizing: border-box;
  border: 2px solid ${theme.colors.input.border.default};
  width: 100%;
  resize: none;

  &:focus {
    outline: none;
    border-color: ${theme.colors.input.border.focus};
    box-shadow: 4px 4px 10px 0px rgba(20, 20, 43, 0.06);
  }

  &::placeholder {
    color: ${theme.colors.text.hint};
  }
`;
